import { useMutation, useSuspenseQuery } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import {
  BootstrapDataImportProjectV2Document,
  type BootstrapDataImportProjectV2Mutation,
  type BootstrapDataImportProjectV2MutationVariables,
  GetDataImportProjectDetailDocument,
  type GetDataImportProjectDetailQuery,
  type GetDataImportProjectDetailQueryVariables,
  GetDataSourcesDocument,
} from "~/features/dataImportProjects/gqls";
import type { DataSourceFormData } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { useActionWithConfirm } from "~/lib/handleActionWithConfirm";
import { rootRoute as R } from "~/routes";
import { JobSchemaMapper } from "./jobSchema.mapper";
import { SheetMapper } from "./schemaDesigner";

type ValidationField = { path?: string | null; message?: string | null };

const formatValidationErrorMessage = (fallbackMessage: string, fields?: ValidationField[] | null) => {
  if (!fields || fields.length === 0) {
    return fallbackMessage;
  }

  const joined = fields
    .filter((field): field is Required<Pick<ValidationField, "message">> & ValidationField => Boolean(field?.message))
    .map((field) =>
      field.path ? `${field.path}: ${field.message ?? "Validation error"}` : (field.message ?? "Validation error"),
    )
    .join(", ");

  return joined || fallbackMessage;
};

export const useSubmitDataSourceDesignerForm = ({
  projectId,
  canExecute,
}: {
  projectId: string;
  canExecute?: () => boolean;
}) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { refetch: refetchProject } = useSuspenseQuery<
    GetDataImportProjectDetailQuery,
    GetDataImportProjectDetailQueryVariables
  >(GetDataImportProjectDetailDocument, {
    variables: { tenantId, where: { id: { _eq: projectId } } },
  });
  const [bootstrapDataImportProjectV2] = useMutation<
    BootstrapDataImportProjectV2Mutation,
    BootstrapDataImportProjectV2MutationVariables
  >(BootstrapDataImportProjectV2Document);
  const handleActionWithConfirm = useActionWithConfirm();

  const handleDataSourceSubmit = useCallback(
    async (formData: DataSourceFormData) => {
      const dataSources = SheetMapper.toDataSourceInputs(projectId, formData);
      const schemaMappingsBySheet = JobSchemaMapper.toBootstrapDataImportProjectV2SchemaMappings(formData);
      const dataSourcePayload = dataSources.map((dataSource) => ({
        dataSource: { ...dataSource, tenantId },
        schemaMappings: schemaMappingsBySheet[dataSources.indexOf(dataSource)] ?? [],
      }));

      const runBootstrapFlow = async (): Promise<void> => {
        const result = await bootstrapDataImportProjectV2({
          variables: {
            input: {
              tenantId,
              dataImportProjectId: projectId,
              dataSources: dataSourcePayload,
            },
          },
          refetchQueries: [
            {
              query: GetDataSourcesDocument,
              variables: { tenantId, where: { dataSourceImportProjects: { dataImportProjectId: { _eq: projectId } } } },
            },
            {
              query: GetDataImportProjectDetailDocument,
              variables: { tenantId, where: { id: { _eq: projectId } } },
            },
          ],
        });

        const response = result.data?.cnutBootstrapDataImportProjectV2;
        if (!response) {
          throw new Error("Bootstrap response is empty");
        }
        if (response.__typename === "ClientError") {
          throw new Error(`Failed to bootstrap data import project: ${response.code} - ${response.message}`);
        }
        if (response.__typename === "ValidationError") {
          const message = formatValidationErrorMessage(response.message, response.fields);
          throw new Error(message);
        }
      };

      return handleActionWithConfirm({
        confirmMessage: "Create data sources?",
        successMessage: "Data sources created successfully",
        canExecute,
        action: async () => {
          setIsSubmitting(true);
          await runBootstrapFlow();
        },
        finallyAction: () => {
          setIsSubmitting(false);
        },
      }).then(async () => {
        await refetchProject();

        const targetPath = R.dataImportProjects.detail.$buildPath({
          params: { id: projectId },
        });
        return navigate(targetPath);
      });
    },
    [bootstrapDataImportProjectV2, canExecute, handleActionWithConfirm, navigate, projectId, refetchProject, tenantId],
  );

  return { handleDataSourceSubmit, isSubmitting };
};
